import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { partyLabel } from "incidents/notifications";
import { documentationSections, FormSectionId, requiredNotificationParties, SectionProgress } from "incidents/sections";
import { hasText } from "incidents/text";
import { formatWhen, fromDateTimeLocal, toDateTimeLocal } from "incidents/time";
import {
  Incident,
  IncidentDocumentation,
  IncidentNotification,
  ReasonableGroundsDecision,
  YesNoUnsure
} from "incidents/types";
import { useMemo, useState } from "react";
import { fieldSx, primaryButtonSx, SectionTitle, SourceTag, usePhrase } from "./common";

const NEURAVUE_CONFIRMS: Array<keyof IncidentDocumentation> = [
  "resident_confirmed",
  "event_time_confirmed",
  "discovered_at_confirmed",
  "location_confirmed",
  "responders_confirmed",
  "prefilled_confirmed",
  "critical_incident_category_confirmed"
];

interface Choice {
  value: string;
  label: string;
}

export function DocumentationForm({
  incident,
  draft,
  setDraft,
  notes,
  setNotes,
  decision,
  setDecision,
  locked,
  canDocument,
  busy,
  actorUserId,
  onSave,
  onFinalize
}: {
  incident: Incident;
  draft: IncidentDocumentation;
  setDraft: (next: IncidentDocumentation) => void;
  notes: IncidentNotification[];
  setNotes: (next: IncidentNotification[]) => void;
  decision: ReasonableGroundsDecision;
  setDecision: (next: ReasonableGroundsDecision) => void;
  locked: boolean;
  canDocument: boolean;
  busy: boolean;
  actorUserId: string;
  onSave: () => void;
  onFinalize: () => void;
}) {
  const phrase = usePhrase();
  const locale = phrase("en", "fr") === "fr" ? "fr" : "en";
  const whenLocale = locale === "fr" ? "fr-CA" : "en-CA";
  const disabled = locked || !canDocument;
  const sections = useMemo(
    () =>
      documentationSections({
        type: incident.type,
        province: incident.province,
        documentation: draft,
        notifications: notes,
        reasonable_grounds_decision: decision
      }),
    [incident.type, incident.province, draft, notes, decision]
  );
  const [openSection, setOpenSection] = useState<FormSectionId | null>(
    () => sections.find((section) => !section.complete)?.id ?? null
  );
  const complete = sections.filter((section) => section.complete).length;

  function missing(id: FormSectionId, field: string): boolean {
    return sections.find((section) => section.id === id)?.missing.includes(field) ?? false;
  }

  function patchDraft(patch: Partial<IncidentDocumentation>) {
    setDraft({ ...draft, ...patch });
  }

  function confirmNeuravue(checked: boolean) {
    setDraft({
      ...draft,
      resident_confirmed: checked,
      event_time_confirmed: checked,
      discovered_at_confirmed: checked,
      location_confirmed: checked,
      responders_confirmed: checked,
      prefilled_confirmed: checked,
      critical_incident_category_confirmed: checked,
      ...(incident.type === "fall" ? { post_fall_instrument_confirmed: checked } : {})
    });
  }

  function clearNeuravueConfirm(patch: Partial<IncidentDocumentation>) {
    setDraft({
      ...draft,
      ...patch,
      resident_confirmed: false,
      event_time_confirmed: false,
      discovered_at_confirmed: false,
      location_confirmed: false,
      responders_confirmed: false,
      prefilled_confirmed: false,
      critical_incident_category_confirmed: false,
      ...(incident.type === "fall" ? { post_fall_instrument_confirmed: false } : {})
    });
  }

  const neuravueConfirmed =
    NEURAVUE_CONFIRMS.every((key) => Boolean(draft[key])) &&
    (incident.type !== "fall" || Boolean(draft.post_fall_instrument_confirmed));

  const titles: Record<FormSectionId, string> = {
    respond: phrase("Respond", "Intervenir"),
    assess: phrase("Assess", "Évaluer"),
    notify: phrase("Notify", "Aviser"),
    document: phrase("Document", "Documenter"),
    review: phrase("Review & finalize", "Réviser et finaliser")
  };

  const hints: Record<FormSectionId, string> = {
    respond: phrase("What you did when you arrived.", "Ce que vous avez fait en arrivant."),
    assess: phrase("What you found on assessment.", "Ce que vous avez constaté à l'évaluation."),
    notify: phrase("Who you told, and when.", "Qui vous avez avisé, et quand."),
    document: phrase("Your own words. There is no generated story.", "Vos propres mots. Il n'y a pas de récit généré."),
    review: phrase(
      "Your judgement, then lock the record here. NeuraVue does not send it.",
      "Votre jugement, puis verrouillez le dossier ici. NeuraVue ne l'envoie pas."
    )
  };

  return (
    <section>
      <SectionTitle
        title={phrase("Documentation", "Documentation")}
        hint={phrase(
          "Work through the sections in order. Open one at a time. NeuraVue does not decide whether abuse occurred.",
          "Suivez les sections dans l'ordre. Ouvrez-en une à la fois. NeuraVue ne décide pas s'il y a eu de l'abus."
        )}
      />
      <Box sx={{ mb: 2 }} role="group" aria-label={phrase("Documentation progress", "Progression de la documentation")}>
        <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
          {phrase(
            `${complete} of ${sections.length} sections complete`,
            `${complete} sections sur ${sections.length} terminées`
          )}
        </Typography>
        <Box
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={sections.length}
          aria-valuenow={complete}
          aria-label={phrase(
            `${complete} of ${sections.length} sections complete`,
            `${complete} sections sur ${sections.length} terminées`
          )}
          sx={{ height: 8, bgcolor: "#E6E8EC", borderRadius: 99 }}
        >
          <Box
            sx={{
              width: `${(complete / sections.length) * 100}%`,
              height: 8,
              bgcolor: complete === sections.length ? COLORS.success : COLORS.primary,
              borderRadius: 99
            }}
          />
        </Box>
      </Box>

      <Box display="grid" gap={1}>
        {sections.map((section, index) => (
          <Accordion
            key={section.id}
            disableGutters
            elevation={0}
            expanded={openSection === section.id}
            TransitionProps={{ unmountOnExit: true }}
            onChange={() => setOpenSection((current) => (current === section.id ? null : section.id))}
            sx={{
              border: `1px solid ${COLORS.surface.border}`,
              borderRadius: 2,
              bgcolor: COLORS.surface.card,
              "&:before": { display: "none" },
              overflow: "hidden"
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls={`${section.id}-panel`}
              id={`${section.id}-header`}
              sx={{ minHeight: 64, "& .MuiAccordionSummary-content": { my: 1.25 } }}
            >
              <Box>
                <Typography component="span" sx={{ fontWeight: 700, fontSize: 18 }}>
                  {index + 1}. {titles[section.id]}
                </Typography>
                <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 14 }}>
                  {hints[section.id]}{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: section.complete ? COLORS.success : COLORS.warning }}
                  >
                    {countLabel(section, phrase)}
                  </Box>
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails id={`${section.id}-panel`} sx={{ display: "grid", gap: 1.5, pt: 0 }}>
              {section.id === "respond" ? (
                <NeuravueSummary
                  incident={incident}
                  draft={draft}
                  disabled={disabled}
                  confirmed={neuravueConfirmed}
                  onConfirm={confirmNeuravue}
                  onCorrect={clearNeuravueConfirm}
                  whenLocale={whenLocale}
                />
              ) : null}
              {section.id === "respond" ? (
                <RespondFields
                  draft={draft}
                  incident={incident}
                  disabled={disabled}
                  missing={(field) => missing("respond", field)}
                  onChange={patchDraft}
                />
              ) : null}
              {section.id === "assess" ? (
                <AssessFields
                  draft={draft}
                  incident={incident}
                  disabled={disabled}
                  missing={(field) => missing("assess", field)}
                  onChange={patchDraft}
                />
              ) : null}
              {section.id === "notify" ? (
                <NotifyFields
                  incident={incident}
                  draft={draft}
                  notes={notes}
                  decision={decision}
                  disabled={disabled}
                  actorUserId={actorUserId}
                  onChange={setNotes}
                />
              ) : null}
              {section.id === "document" ? (
                <DocumentFields
                  draft={draft}
                  incident={incident}
                  decision={decision}
                  disabled={disabled}
                  missing={(field) => missing("document", field)}
                  onChange={patchDraft}
                />
              ) : null}
              {section.id === "review" ? (
                <ReviewFields
                  draft={draft}
                  incident={incident}
                  decision={decision}
                  disabled={disabled}
                  missing={(field) => missing("review", field)}
                  onDraft={patchDraft}
                  onDecision={setDecision}
                />
              ) : null}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {!locked && canDocument ? (
        <Box display="flex" gap={1} mt={2} flexWrap="wrap">
          <Button
            variant="contained"
            disabled={busy}
            sx={{ ...primaryButtonSx, ...primaryContainedButtonSx }}
            onClick={onSave}
          >
            {phrase("Save draft", "Enregistrer le brouillon")}
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            sx={{ ...primaryButtonSx, ...primaryContainedButtonSx }}
            onClick={onFinalize}
          >
            {phrase("Finalize and lock", "Finaliser et verrouiller")}
          </Button>
        </Box>
      ) : null}
      <Typography sx={{ mt: 1, color: COLORS.surface.secondaryText }}>
        {phrase(
          "Finalizing locks the record here. It does not send it to the ministry, the police, or the family.",
          "Finaliser verrouille le dossier ici. Cela ne l'envoie pas au ministère, à la police ou à la famille."
        )}
      </Typography>
    </section>
  );
}

function countLabel(section: SectionProgress, phrase: (en: string, fr: string) => string): string {
  if (section.remaining === 0) return phrase("Done", "Fait");
  if (section.remaining === 1) return phrase("1 required", "1 requis");
  return phrase(`${section.remaining} required`, `${section.remaining} requis`);
}

function NeuravueSummary({
  incident,
  draft,
  disabled,
  confirmed,
  onConfirm,
  onCorrect,
  whenLocale
}: {
  incident: Incident;
  draft: IncidentDocumentation;
  disabled: boolean;
  confirmed: boolean;
  onConfirm: (checked: boolean) => void;
  onCorrect: (patch: Partial<IncidentDocumentation>) => void;
  whenLocale: string;
}) {
  const phrase = usePhrase();
  const [details, setDetails] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const staff =
    incident.notified_staff.map((person) => `${person.name} (${person.role})`).join("; ") ||
    phrase("No staff names on the alert", "Aucun nom de personnel sur l'alerte");
  const summary = [draft.resident_name || "—", formatWhen(draft.event_time, whenLocale), draft.location || "—"].join(
    " · "
  );

  return (
    <Box sx={{ p: 1.5, bgcolor: "#F4F5F7", borderRadius: 2 }}>
      <Box display="flex" justifyContent="space-between" gap={1} flexWrap="wrap" alignItems="flex-start">
        <Box>
          <SourceTag filledBySystem />
          <Typography sx={{ mt: 0.75 }}>{summary}</Typography>
        </Box>
        <Button
          sx={primaryButtonSx}
          aria-expanded={details}
          aria-controls="neuravue-facts"
          onClick={() => setDetails((open) => !open)}
        >
          {details ? phrase("Hide details", "Masquer les détails") : phrase("Show details", "Voir les détails")}
        </Button>
      </Box>
      <FormControlLabel
        control={<Checkbox checked={confirmed} disabled={disabled} onChange={(_, checked) => onConfirm(checked)} />}
        label={phrase("I confirm these facts filled by NeuraVue", "Je confirme ces faits remplis par NeuraVue")}
      />
      {details ? (
        <Box id="neuravue-facts" display="grid" gap={0.75} mt={1}>
          <Fact label={phrase("Resident", "Usager")} value={draft.resident_name || "—"} />
          <Fact label={phrase("Event time", "Heure de l'événement")} value={formatWhen(draft.event_time, whenLocale)} />
          <Fact
            label={phrase("Time staff discovered it", "Heure de constat")}
            value={formatWhen(draft.discovered_at, whenLocale)}
          />
          <Fact label={phrase("Exact location", "Lieu exact")} value={draft.location || "—"} />
          <Fact label={phrase("Staff who responded", "Personnel qui est intervenu")} value={staff} />
          {incident.type === "fall" ? (
            <Fact
              label={phrase("Post-fall tool name and version", "Outil post-chute, nom et version")}
              value={draft.post_fall_instrument || "—"}
            />
          ) : null}
          <Fact
            label={phrase("Incident category", "Catégorie d'incident")}
            value={draft.critical_incident_category || "—"}
          />
          {!disabled ? (
            <Button sx={{ ...primaryButtonSx, justifySelf: "start" }} onClick={() => setCorrecting((open) => !open)}>
              {correcting
                ? phrase("Hide corrections", "Masquer les corrections")
                : phrase("Correct a fact", "Corriger un fait")}
            </Button>
          ) : null}
          {correcting && !disabled ? (
            <Box display="grid" gap={1.5} mt={1}>
              <DocField
                label={phrase("Resident", "Usager")}
                value={draft.resident_name || ""}
                onChange={(value) => onCorrect({ resident_name: value })}
              />
              <DocField
                label={phrase("Event time", "Heure de l'événement")}
                value={toDateTimeLocal(draft.event_time)}
                type="datetime-local"
                onChange={(value) => onCorrect({ event_time: fromDateTimeLocal(value) })}
              />
              <DocField
                label={phrase("Time staff discovered it", "Heure de constat")}
                value={toDateTimeLocal(draft.discovered_at)}
                type="datetime-local"
                onChange={(value) => onCorrect({ discovered_at: fromDateTimeLocal(value) })}
              />
              <DocField
                label={phrase("Exact location", "Lieu exact")}
                value={draft.location || ""}
                onChange={(value) => onCorrect({ location: value })}
              />
              {incident.type === "fall" ? (
                <DocField
                  label={phrase("Post-fall tool name and version", "Outil post-chute, nom et version")}
                  value={draft.post_fall_instrument || ""}
                  onChange={(value) => onCorrect({ post_fall_instrument: value })}
                />
              ) : null}
              <DocField
                label={phrase("Incident category", "Catégorie d'incident")}
                value={draft.critical_incident_category || ""}
                onChange={(value) => onCorrect({ critical_incident_category: value })}
              />
              <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 14 }}>
                {phrase(
                  "Changing a fact clears the confirmation. Confirm the summary again.",
                  "Modifier un fait enlève la confirmation. Confirmez le résumé de nouveau."
                )}
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function RespondFields({ draft, incident, disabled, missing, onChange }: FieldProps & { incident: Incident }) {
  const phrase = usePhrase();
  const showSafety = incident.type === "unsafe_interaction" || incident.type === "responsive_behaviour";
  return (
    <>
      <DocField
        label={phrase("Witnessed by staff?", "Vu par le personnel?")}
        value={draft.witnessed || ""}
        select={yesNo(phrase)}
        disabled={disabled}
        required={missing("witnessed")}
        staff
        onChange={(value) => onChange({ witnessed: value as IncidentDocumentation["witnessed"] })}
      />
      {draft.witnessed === "yes" ? (
        <DocField
          label={phrase("Who witnessed it", "Qui l'a vu")}
          value={draft.witnessed_by || ""}
          disabled={disabled}
          required={missing("witnessed_by")}
          staff
          onChange={(value) => onChange({ witnessed_by: value })}
        />
      ) : null}
      <DocField
        label={phrase("Care given and by whom", "Soins donnés et par qui")}
        value={draft.care_given || ""}
        multiline
        disabled={disabled}
        required={missing("care_given")}
        staff
        onChange={(value) => onChange({ care_given: value })}
      />
      {showSafety ? (
        <DocField
          label={phrase("Safety measures", "Mesures de sécurité")}
          value={draft.safety_measures || ""}
          multiline
          disabled={disabled}
          required={missing("safety_measures")}
          staff
          onChange={(value) => onChange({ safety_measures: value })}
        />
      ) : null}
      {incident.type === "responsive_behaviour" ? (
        <DocField
          label={phrase("Intervention and the resident's response", "Intervention et réponse de l'usager")}
          value={draft.intervention || ""}
          multiline
          disabled={disabled}
          required={missing("intervention")}
          staff
          onChange={(value) => onChange({ intervention: value })}
        />
      ) : null}
      <DocField
        label={phrase("Transferred to hospital?", "Transfert à l'hôpital?")}
        value={draft.hospital_transfer || ""}
        select={yesNo(phrase)}
        disabled={disabled}
        required={missing("hospital_transfer")}
        staff
        onChange={(value) => onChange({ hospital_transfer: value as IncidentDocumentation["hospital_transfer"] })}
      />
      {draft.hospital_transfer === "yes" ? (
        <DocField
          label={phrase("Hospital", "Hôpital")}
          value={draft.hospital_name || ""}
          disabled={disabled}
          required={missing("hospital_name")}
          staff
          onChange={(value) => onChange({ hospital_name: value })}
        />
      ) : null}
    </>
  );
}

function AssessFields({ draft, incident, disabled, missing, onChange }: FieldProps & { incident: Incident }) {
  const phrase = usePhrase();
  const clinical = incident.type === "fall" || incident.type === "unsafe_interaction";
  return (
    <>
      {clinical ? (
        <DocField
          label={phrase("Level of consciousness", "Niveau de conscience")}
          value={draft.consciousness || ""}
          disabled={disabled}
          required={missing("consciousness")}
          staff
          onChange={(value) => onChange({ consciousness: value })}
        />
      ) : null}
      {incident.type === "fall" ? (
        <DocField
          label={phrase("Vital signs and time", "Signes vitaux et heure")}
          value={draft.vitals || ""}
          disabled={disabled}
          required={missing("vitals")}
          staff
          onChange={(value) => onChange({ vitals: value })}
        />
      ) : null}
      {clinical ? (
        <DocField
          label={phrase("Pain", "Douleur")}
          value={draft.pain || ""}
          disabled={disabled}
          required={missing("pain")}
          staff
          onChange={(value) => onChange({ pain: value })}
        />
      ) : null}
      {clinical ? (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(draft.injuries_none)}
              disabled={disabled}
              onChange={(_, checked) => onChange({ injuries_none: checked })}
            />
          }
          label={
            <span>
              {phrase("No injury observed", "Aucune blessure observée")}
              {missing("injuries") ? <RequiredWord /> : null}
            </span>
          }
        />
      ) : null}
      {clinical ? (
        <DocField
          label={phrase("Injuries (describe, do not attach photos)", "Blessures (décrire, sans photo)")}
          value={draft.injuries || ""}
          multiline
          disabled={disabled}
          required={missing("injuries") && !draft.injuries_none}
          staff
          onChange={(value) => onChange({ injuries: value })}
        />
      ) : null}
      {incident.type === "unsafe_interaction" || incident.type === "responsive_behaviour" ? (
        <DocField
          label={phrase("Distress observed", "Détresse observée")}
          value={draft.distress || ""}
          disabled={disabled}
          required={missing("distress")}
          staff
          onChange={(value) => onChange({ distress: value })}
        />
      ) : null}
      {incident.type === "responsive_behaviour" ? (
        <DocField
          label={phrase("Triggers", "Déclencheurs")}
          value={draft.triggers || ""}
          disabled={disabled}
          required={missing("triggers")}
          staff
          onChange={(value) => onChange({ triggers: value })}
        />
      ) : null}
      {incident.type === "fall" ? (
        <DocField
          label={phrase("Post-fall assessment completed", "Évaluation post-chute terminée")}
          value={toDateTimeLocal(draft.post_fall_completed_at)}
          type="datetime-local"
          disabled={disabled}
          required={missing("post_fall_completed_at")}
          staff
          onChange={(value) => onChange({ post_fall_completed_at: fromDateTimeLocal(value) })}
        />
      ) : null}
      {incident.type === "wandering_zone" ? (
        <>
          <DocField
            label={phrase("Last seen", "Vu la dernière fois")}
            value={toDateTimeLocal(draft.missing_last_seen)}
            type="datetime-local"
            disabled={disabled}
            required={missing("missing_last_seen")}
            staff
            onChange={(value) => onChange({ missing_last_seen: fromDateTimeLocal(value) })}
          />
          <DocField
            label={phrase("Found at", "Retrouvé à")}
            value={toDateTimeLocal(draft.missing_found_time)}
            type="datetime-local"
            disabled={disabled}
            required={missing("missing_found_time")}
            staff
            onChange={(value) => onChange({ missing_found_time: fromDateTimeLocal(value) })}
          />
          <DocField
            label={phrase("Found where", "Retrouvé où")}
            value={draft.missing_found_location || ""}
            disabled={disabled}
            required={missing("missing_found_location")}
            staff
            onChange={(value) => onChange({ missing_found_location: value })}
          />
          <DocField
            label={phrase("Returned with injury?", "Retour avec blessure?")}
            value={draft.returned_injured || ""}
            select={yesNo(phrase)}
            disabled={disabled}
            staff
            onChange={(value) => onChange({ returned_injured: value as IncidentDocumentation["returned_injured"] })}
          />
        </>
      ) : null}
      {incident.type === "fall" ? (
        <DocField
          label={phrase("Significant change in condition", "Changement important de l'état")}
          value={draft.significant_change || ""}
          select={yesNoUnknown(phrase)}
          disabled={disabled}
          staff
          onChange={(value) => onChange({ significant_change: value as IncidentDocumentation["significant_change"] })}
        />
      ) : null}
      {!clinical && incident.type !== "wandering_zone" && incident.type !== "responsive_behaviour" ? (
        <Typography sx={{ color: COLORS.surface.secondaryText }}>
          {phrase(
            "No extra assessment is required for this alert. Use Document for what you saw.",
            "Aucune évaluation supplémentaire n'est requise pour cette alerte. Utilisez Documenter pour ce que vous avez vu."
          )}
        </Typography>
      ) : null}
    </>
  );
}

function NotifyFields({
  incident,
  draft,
  notes,
  decision,
  disabled,
  actorUserId,
  onChange
}: {
  incident: Incident;
  draft: IncidentDocumentation;
  notes: IncidentNotification[];
  decision: ReasonableGroundsDecision;
  disabled: boolean;
  actorUserId: string;
  onChange: (next: IncidentNotification[]) => void;
}) {
  const phrase = usePhrase();
  const locale = phrase("en", "fr") === "fr" ? "fr" : "en";
  const required = new Set(
    requiredNotificationParties({
      type: incident.type,
      province: incident.province,
      documentation: draft,
      notifications: notes,
      reasonable_grounds_decision: decision
    })
  );

  function update(index: number, patch: Partial<IncidentNotification>) {
    const next = notes.slice();
    next[index] = { ...next[index], ...patch, recorded_by: actorUserId };
    onChange(next);
  }

  return (
    <>
      <Typography sx={{ color: COLORS.surface.secondaryText }}>
        {phrase(
          "Suggested rows are reminders, not completed calls. Required rows need a name and a time.",
          "Les lignes suggérées sont des rappels, pas des appels faits. Les lignes requises ont besoin d'un nom et d'une heure."
        )}
      </Typography>
      {notes.map((note, index) => {
        const needed = required.has(note.party);
        const done = Boolean(note.done && note.at && hasText(note.person_name));
        return (
          <Box key={note.id} sx={{ p: 1.5, border: `1px solid ${COLORS.surface.border}`, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>
              {partyLabel(note.party, locale)}
              {needed && !done ? <RequiredWord /> : null}
              {!needed ? (
                <Box component="span" sx={{ fontWeight: 600, color: COLORS.surface.secondaryText, ml: 1 }}>
                  {phrase("Reminder", "Rappel")}
                </Box>
              ) : null}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={note.done}
                    disabled={disabled}
                    onChange={(_, checked) =>
                      update(index, { done: checked, at: checked ? note.at || new Date().toISOString() : note.at })
                    }
                  />
                }
                label={phrase("Done", "Fait")}
              />
              <TextField
                label={phrase("Name", "Nom")}
                value={note.person_name || ""}
                disabled={disabled}
                onChange={(event) => update(index, { person_name: event.target.value })}
                sx={fieldSx}
              />
              <TextField
                label={phrase("Time", "Heure")}
                type="datetime-local"
                value={toDateTimeLocal(note.at)}
                disabled={disabled}
                onChange={(event) => update(index, { at: fromDateTimeLocal(event.target.value) })}
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
              <TextField
                select
                label={phrase("Method", "Moyen")}
                value={note.method || ""}
                disabled={disabled}
                onChange={(event) => update(index, { method: event.target.value as IncidentNotification["method"] })}
                sx={{ ...fieldSx, minWidth: 160 }}
              >
                <MenuItem value="">{phrase("Choose", "Choisir")}</MenuItem>
                <MenuItem value="in_person">{phrase("In person", "En personne")}</MenuItem>
                <MenuItem value="phone">{phrase("Phone", "Téléphone")}</MenuItem>
                <MenuItem value="app">{phrase("App", "Application")}</MenuItem>
                <MenuItem value="email">{phrase("Email", "Courriel")}</MenuItem>
              </TextField>
              <TextField
                label={phrase("Reference or note", "Référence ou note")}
                value={note.reference_no || note.note || ""}
                disabled={disabled}
                onChange={(event) => update(index, { note: event.target.value })}
                sx={fieldSx}
              />
            </Box>
          </Box>
        );
      })}
    </>
  );
}

function DocumentFields({
  draft,
  incident,
  decision,
  disabled,
  missing,
  onChange
}: FieldProps & { incident: Incident; decision: ReasonableGroundsDecision }) {
  const phrase = usePhrase();
  const carePlan =
    incident.type === "fall" || incident.type === "responsive_behaviour" || incident.type === "wandering_zone";
  return (
    <>
      <DocField
        label={phrase("What I observed", "Ce que j'ai observé")}
        value={draft.observed || ""}
        multiline
        disabled={disabled}
        required={missing("observed")}
        staff
        onChange={(value) => onChange({ observed: value })}
      />
      <DocField
        label={phrase("What led up to it", "Ce qui a mené à l'événement")}
        value={draft.leading_events || ""}
        multiline
        disabled={disabled}
        required={missing("leading_events")}
        staff
        onChange={(value) => onChange({ leading_events: value })}
      />
      {incident.type === "unsafe_interaction" ? (
        <DocField
          label={phrase("Resident's own words", "Mots de l'usager")}
          value={draft.resident_statement || ""}
          multiline
          disabled={disabled}
          required={missing("resident_statement")}
          staff
          onChange={(value) => onChange({ resident_statement: value })}
        />
      ) : null}
      {incident.type === "unsafe_interaction" ? (
        <DocField
          label={phrase("Other residents involved", "Autres usagers concernés")}
          value={draft.other_residents || ""}
          disabled={disabled}
          required={missing("other_residents")}
          staff
          onChange={(value) => onChange({ other_residents: value })}
        />
      ) : null}
      <DocField
        label={phrase("People present or who discovered it", "Personnes présentes ou qui l'ont découvert")}
        value={draft.persons_present || ""}
        disabled={disabled}
        required={missing("persons_present")}
        staff
        onChange={(value) => onChange({ persons_present: value })}
      />
      <DocField
        label={phrase(
          "What we did right away to prevent a repeat",
          "Mesures immédiates pour éviter que cela se reproduise"
        )}
        value={draft.immediate_prevention || ""}
        multiline
        disabled={disabled}
        required={missing("immediate_prevention")}
        staff
        onChange={(value) => onChange({ immediate_prevention: value })}
      />
      <DocField
        label={phrase("Longer-term actions", "Mesures à plus long terme")}
        value={draft.long_term_actions || ""}
        multiline
        disabled={disabled}
        required={missing("long_term_actions")}
        staff
        onChange={(value) => onChange({ long_term_actions: value })}
      />
      {carePlan ? (
        <DocField
          label={phrase("Plan of care updated?", "Plan de soins mis à jour?")}
          value={draft.care_plan_updated || ""}
          disabled={disabled}
          required={missing("care_plan_updated")}
          staff
          onChange={(value) => onChange({ care_plan_updated: value })}
        />
      ) : null}
      <DocField
        label={phrase("How people are now", "État actuel des personnes")}
        value={draft.outcome || ""}
        multiline
        disabled={disabled}
        required={missing("outcome")}
        staff
        onChange={(value) => onChange({ outcome: value })}
      />
      {incident.type === "unsafe_interaction" && decision.decision === "yes" ? (
        <>
          <DocField
            label={phrase("Investigation lead", "Responsable de l'enquête")}
            value={draft.investigation_lead || ""}
            disabled={disabled}
            required={missing("investigation_lead")}
            staff
            onChange={(value) => onChange({ investigation_lead: value })}
          />
          <DocField
            label={phrase("Investigation started", "Enquête commencée")}
            value={toDateTimeLocal(draft.investigation_start)}
            type="datetime-local"
            disabled={disabled}
            staff
            onChange={(value) => onChange({ investigation_start: fromDateTimeLocal(value) })}
          />
        </>
      ) : null}
    </>
  );
}

function ReviewFields({
  draft,
  incident,
  decision,
  disabled,
  missing,
  onDraft,
  onDecision
}: {
  draft: IncidentDocumentation;
  incident: Incident;
  decision: ReasonableGroundsDecision;
  disabled: boolean;
  missing: (field: string) => boolean;
  onDraft: (patch: Partial<IncidentDocumentation>) => void;
  onDecision: (next: ReasonableGroundsDecision) => void;
}) {
  const phrase = usePhrase();
  return (
    <>
      <Typography>
        {phrase(
          "NeuraVue does not decide whether abuse occurred. This answer is your professional judgement.",
          "NeuraVue ne décide pas s'il y a eu de l'abus. Cette réponse est votre jugement professionnel."
        )}
      </Typography>
      <TextField
        select
        fullWidth
        label={phrase(
          "Reasonable grounds to suspect abuse, neglect, improper care, or unlawful conduct?",
          "Motifs raisonnables de soupçonner abus, négligence, soins inadéquats ou conduite illégale?"
        )}
        value={decision.decision || ""}
        disabled={disabled}
        onChange={(event) =>
          onDecision({
            ...decision,
            decision: (event.target.value || null) as ReasonableGroundsDecision["decision"]
          })
        }
        sx={fieldSx}
        helperText={missing("reasonable_grounds") ? phrase("Required", "Requis") : undefined}
      >
        <MenuItem value="">{phrase("Not answered", "Sans réponse")}</MenuItem>
        <MenuItem value="yes">{phrase("Yes", "Oui")}</MenuItem>
        <MenuItem value="no">{phrase("No", "Non")}</MenuItem>
        <MenuItem value="unsure">
          {phrase("Unsure — escalate to the nurse in charge", "Incertain — escalader à l'infirmière responsable")}
        </MenuItem>
      </TextField>
      {decision.decision === "yes" ? (
        <DocField
          label={phrase("Suspicion and the facts it is based on", "Soupçon et faits sur lesquels il repose")}
          value={decision.suspicion || ""}
          multiline
          disabled={disabled}
          required={missing("suspicion")}
          staff
          onChange={(value) => onDecision({ ...decision, suspicion: value })}
        />
      ) : null}
      <DocField
        label={phrase("Could this be a criminal offence?", "Cela pourrait-il être une infraction criminelle?")}
        value={draft.criminal_offence || ""}
        select={yesNoUnsure(phrase)}
        disabled={disabled}
        staff
        onChange={(value) => onDraft({ criminal_offence: value as YesNoUnsure | "" })}
      />
      <DocField
        label={phrase(
          "Is the substitute decision-maker the person believed responsible?",
          "Le représentant est-il la personne qu'on croit responsable?"
        )}
        value={draft.sdm_responsible || ""}
        select={yesNo(phrase)}
        disabled={disabled}
        staff
        onChange={(value) => onDraft({ sdm_responsible: value as IncidentDocumentation["sdm_responsible"] })}
      />
      {incident.province === "QC" ? (
        <DocField
          label={phrase("AH-223-1 required?", "AH-223-1 requis?")}
          value={draft.qc_ah223_required || ""}
          select={[
            { value: "", label: phrase("Choose", "Choisir") },
            { value: "incident", label: phrase("Incident", "Incident") },
            { value: "accident", label: phrase("Accident", "Accident") },
            { value: "no", label: phrase("No", "Non") }
          ]}
          disabled={disabled}
          required={missing("qc_ah223")}
          staff
          onChange={(value) => onDraft({ qc_ah223_required: value as IncidentDocumentation["qc_ah223_required"] })}
          confirmed={Boolean(draft.qc_ah223_confirmed)}
          onConfirm={(value) => onDraft({ qc_ah223_confirmed: value })}
          confirmLabel={phrase("I confirm this", "Je confirme")}
        />
      ) : null}
      {draft.late_entry ? (
        <DocField
          label={phrase("Late entry reason", "Raison de la note tardive")}
          value={draft.late_entry_reason || ""}
          disabled={disabled}
          required={missing("late_entry_reason")}
          staff
          onChange={(value) => onDraft({ late_entry_reason: value })}
        />
      ) : null}
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(draft.attestation_accepted)}
            disabled={disabled}
            onChange={(_, checked) => onDraft({ attestation_accepted: checked })}
          />
        }
        label={
          <span>
            {phrase(
              "I attest this record reflects my own observations and actions, is accurate to the best of my knowledge, and I understand NeuraVue does not determine whether abuse occurred.",
              "J'atteste que ce dossier reflète mes propres observations et actions, qu'il est exact au mieux de ma connaissance, et que NeuraVue ne détermine pas s'il y a eu de l'abus."
            )}
            {missing("attestation_accepted") ? <RequiredWord /> : null}
          </span>
        }
      />
      <DocField
        label={phrase("Your name", "Votre nom")}
        value={draft.attestation_name || ""}
        disabled={disabled}
        required={missing("attestation_name")}
        staff
        onChange={(value) => onDraft({ attestation_name: value })}
      />
      <DocField
        label={phrase("Designation (RN, RPN, NP, PSW)", "Titre (inf., IPS, PAB)")}
        value={draft.attestation_designation || ""}
        disabled={disabled}
        required={missing("attestation_designation")}
        staff
        onChange={(value) => onDraft({ attestation_designation: value })}
      />
      <DocField
        label={phrase(
          "Official channel you used (NeuraVue does not send this)",
          "Voie officielle que vous avez utilisée (NeuraVue ne l'envoie pas)"
        )}
        value={draft.official_channel || ""}
        disabled={disabled}
        staff
        onChange={(value) => onDraft({ official_channel: value })}
      />
      <DocField
        label={phrase("Reference number, if you already submitted", "Numéro de référence, si vous avez déjà soumis")}
        value={draft.official_reference || ""}
        disabled={disabled}
        staff
        onChange={(value) => onDraft({ official_reference: value })}
      />
    </>
  );
}

interface FieldProps {
  draft: IncidentDocumentation;
  disabled: boolean;
  missing: (field: string) => boolean;
  onChange: (patch: Partial<IncidentDocumentation>) => void;
}

function yesNo(phrase: (en: string, fr: string) => string): Choice[] {
  return [
    { value: "", label: phrase("Choose", "Choisir") },
    { value: "yes", label: phrase("Yes", "Oui") },
    { value: "no", label: phrase("No", "Non") }
  ];
}

function yesNoUnknown(phrase: (en: string, fr: string) => string): Choice[] {
  return [...yesNo(phrase), { value: "unknown", label: phrase("Unknown", "Inconnu") }];
}

function yesNoUnsure(phrase: (en: string, fr: string) => string): Choice[] {
  return [
    { value: "", label: phrase("Choose", "Choisir") },
    { value: "yes", label: phrase("Yes", "Oui") },
    { value: "no", label: phrase("No", "Non") },
    { value: "unsure", label: phrase("Unsure", "Incertain") }
  ];
}

function RequiredWord() {
  const phrase = usePhrase();
  return (
    <Box component="span" sx={{ color: COLORS.error, fontWeight: 700, fontSize: 13, ml: 1 }}>
      {" "}
      {phrase("Required", "Requis")}
    </Box>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Typography>
      <strong>{label}: </strong>
      {value}
    </Typography>
  );
}

function DocField({
  label,
  value,
  onChange,
  disabled,
  multiline,
  type,
  select,
  staff,
  required,
  confirmed,
  onConfirm,
  confirmLabel
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  multiline?: boolean;
  type?: string;
  select?: Choice[];
  staff?: boolean;
  required?: boolean;
  confirmed?: boolean;
  onConfirm?: (value: boolean) => void;
  confirmLabel?: string;
}) {
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" gap={1} mb={0.5} alignItems="center">
        <Typography component="span" sx={{ fontWeight: 600 }}>
          {label}
          {required ? <RequiredWord /> : null}
        </Typography>
        <SourceTag filledBySystem={!staff} />
      </Box>
      <TextField
        fullWidth
        label={label}
        value={value}
        disabled={disabled}
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        type={type}
        select={Boolean(select)}
        onChange={(event) => onChange(event.target.value)}
        InputLabelProps={type === "datetime-local" ? { shrink: true } : undefined}
        sx={fieldSx}
        inputProps={required ? { "aria-required": true } : undefined}
      >
        {select?.map((option) => (
          <MenuItem key={option.value || "blank"} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      {onConfirm ? (
        <FormControlLabel
          control={
            <Checkbox checked={Boolean(confirmed)} disabled={disabled} onChange={(_, checked) => onConfirm(checked)} />
          }
          label={confirmLabel}
        />
      ) : null}
    </Box>
  );
}
